import { FormControl, FormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { of } from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    FundOrderStatusesEnum,
    FundOrdersService,
    FundOrderReportModel,
    FundOrderStatusTriggerModel,
    FundOrderStatusTriggerInterface
} from '@spa/api';

import { RequiredValidatorKey } from '@spa/validators';

import { Helper, RadioGroupControl } from '../../../../shared/classes';
import { RadioGroupOptionInterface } from '../../../../shared/components';

import { FundOrdersFormErrorStatesEnum } from '../../enums';

import {
    OrderStatusControlFundingOptions,
    OrderStatusesControl
} from '../../../../../assets/descriptors';

export class FundOrderStatusesForm {
    private readonly ordersService: FundOrdersService;

    public formGroup: FormGroup;
    public control: RadioGroupControl;

    constructor(ordersService: FundOrdersService) {
        this.ordersService = ordersService;
    }

    private mapStatusControlOptions(
        order: FundOrderReportModel,
        triggers: number[],
        currentTrigger: number,
        controls: RadioGroupOptionInterface[],
        disabledOption: boolean
    ): RadioGroupOptionInterface[] {
        return controls.map((option: RadioGroupOptionInterface) => {
            const trigger = Helper.enumsHelper()
                .getOrderStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledOption) {
                option.disabled = true;
            }

            if (option.value === order.orderStatus) {
                option.checked = true;

                this.control.value = order.orderStatus;
            }

            return option;
        });
    }

    initForm(order: FundOrderReportModel, orderRequestTriggers: number[], disabledControlItems: boolean): void {
        this.formGroup = new FormGroup({});
        this.control = OrderStatusesControl();

        const currentTrigger = Helper.enumsHelper()
            .getOrderStatusTriggerByStatus(order.orderStatus);

        const options = OrderStatusControlFundingOptions();

        this.control.options = this.mapStatusControlOptions(
            order,
            orderRequestTriggers,
            currentTrigger,
            options,
            disabledControlItems
        );

        this.formGroup.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    validateForm(messagesFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        if (this.formGroup.invalid) {
            const control = this.formGroup.controls[this.control.name];

            this.control.errors = Object.keys(control.errors).map((key: string) => {
                if (key === RequiredValidatorKey) {
                    return 'Different status must be selected.';
                }

                return messagesFunction(this.control.placeholder)[key];
            });

            return false;
        }

        return true;
    }

    createRequest(
        order: FundOrderReportModel,
        updateStatusToCancelledPermissions: string[],
        authUserPermissions: string[]
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        const status = this.formGroup.value[this.control.name];

        if (
            (status === FundOrderStatusesEnum.Rejected) &&
            !Helper.getUnionStrategyPermissionsCheck(updateStatusToCancelledPermissions, authUserPermissions)
        ) {
            const error = new ApiError(FundOrdersFormErrorStatesEnum.UpdateStatusToCancelledRejected);
            const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, [error]);

            return of(errorResponse);
        }

        if (order.orderStatus !== status) {

            const data: FundOrderStatusTriggerInterface = {
                orderStatus: Helper.enumsHelper().getOrderStatusTriggerByStatus(status),
                orderId: order.orderId,
                accountReferenceId: order.accountReferenceId
            };

            const model = new FundOrderStatusTriggerModel(data);

            return this.ordersService.changeOrderStatus(model)
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse(
                                response,
                                FundOrdersFormErrorStatesEnum.UpdateStatusFailed
                            );
                        }

                        order.orderStatus = status;

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
